import type {
  BrowserMultipartResumePlan,
  BrowserMultipartUploadPart,
  BrowserMultipartUploadPlan,
  PresignedUploadResult,
  UploadFileResult,
} from './types';

const API_STORAGE_PRESIGN = '/api/storage/presign';
const API_STORAGE_MULTIPART_RESUME = '/api/storage/multipart/resume';
const API_STORAGE_MULTIPART_COMPLETE = '/api/storage/multipart/complete';
const API_STORAGE_MULTIPART_ABORT = '/api/storage/multipart/abort';

const PRESIGN_REQUEST_TIMEOUT_MS = 15_000;
const DIRECT_UPLOAD_TIMEOUT_MS = 60_000;
const MULTIPART_PART_TIMEOUT_MS = 5 * 60_000;
const MULTIPART_UPLOAD_CONCURRENCY = 3;
const MULTIPART_PART_MAX_ATTEMPTS = 3;
const MULTIPART_SESSION_RESUME_MAX_ATTEMPTS = 2;
type UploadRequestStage = 'presign' | 'direct-upload';

export type BrowserUploadProgress = {
  uploadedBytes: number;
  totalBytes: number;
  percent: number;
};

export type BrowserUploadOptions = {
  onProgress?: (progress: BrowserUploadProgress) => void;
};

function emitUploadProgress(
  file: File,
  uploadedBytes: number,
  onProgress?: BrowserUploadOptions['onProgress']
) {
  if (!onProgress) return;
  const normalizedUploadedBytes = Math.min(
    file.size,
    Math.max(0, uploadedBytes)
  );
  onProgress({
    uploadedBytes: normalizedUploadedBytes,
    totalBytes: file.size,
    percent:
      file.size > 0
        ? Math.round((normalizedUploadedBytes / file.size) * 100)
        : 100,
  });
}

function getMultipartPartBytes(
  file: File,
  plan: BrowserMultipartUploadPlan,
  partNumber: number
) {
  const start = (partNumber - 1) * plan.partSize;
  const end = Math.min(start + plan.partSize, file.size);
  return Math.max(0, end - start);
}

function getCompletedMultipartBytes(
  file: File,
  plan: BrowserMultipartUploadPlan,
  completedPartNumbers: Iterable<number>
) {
  let total = 0;
  for (const partNumber of completedPartNumbers) {
    total += getMultipartPartBytes(file, plan, partNumber);
  }
  return Math.min(file.size, total);
}

export class UploadRequestError extends Error {
  status: number;
  stage: UploadRequestStage;

  constructor(
    message: string,
    status = 500,
    stage: UploadRequestStage = 'presign'
  ) {
    super(message);
    this.name = 'UploadRequestError';
    this.status = status;
    this.stage = stage;
  }
}

export function isUploadRequestError(
  error: unknown
): error is UploadRequestError {
  return error instanceof UploadRequestError;
}

function withTimeoutSignal(timeoutMs: number): {
  signal: AbortSignal;
  clear: () => void;
} {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    clear: () => clearTimeout(timeout),
  };
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function getUploadTimeoutMessage(stage: UploadRequestStage) {
  if (stage === 'presign') {
    return 'Timed out while preparing the upload';
  }
  if (stage === 'direct-upload') {
    return 'Timed out while uploading to storage';
  }
  return 'Timed out while uploading to storage';
}

async function parseApiError(
  response: Response,
  fallbackMessage: string,
  stage: UploadRequestStage
): Promise<UploadRequestError> {
  if (response.status === 413) {
    return new UploadRequestError(
      'File size exceeds the server limit',
      413,
      stage
    );
  }

  let errorMessage = fallbackMessage;
  try {
    const errorData = (await response.json()) as {
      error?: string;
      message?: string;
    };
    errorMessage = errorData.error || errorData.message || fallbackMessage;
  } catch {
    errorMessage = fallbackMessage;
  }
  return new UploadRequestError(errorMessage, response.status, stage);
}

async function requestPresignedUpload(
  file: File,
  folder?: string,
  guestTask?: {
    pendingRunId: string;
    inputSlotId: string;
    paymentIntentId?: string;
    guestRecoveryToken?: string;
  }
): Promise<PresignedUploadResult | BrowserMultipartUploadPlan> {
  const timeout = withTimeoutSignal(PRESIGN_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(
      guestTask
        ? '/api/payment/task/guest/upload/presign'
        : API_STORAGE_PRESIGN,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          size: file.size,
          ...(!guestTask ? { folder: folder || null } : {}),
          ...(guestTask
            ? {
                pendingRunId: guestTask.pendingRunId,
                inputSlotId: guestTask.inputSlotId,
                ...(guestTask.paymentIntentId
                  ? { paymentIntentId: guestTask.paymentIntentId }
                  : {}),
                ...(guestTask.guestRecoveryToken
                  ? { guestRecoveryToken: guestTask.guestRecoveryToken }
                  : {}),
              }
            : {}),
        }),
        signal: timeout.signal,
      }
    );

    if (!response.ok) {
      throw await parseApiError(
        response,
        'Failed to create upload URL',
        'presign'
      );
    }

    return (await response.json()) as PresignedUploadResult;
  } catch (error) {
    if (isAbortError(error)) {
      throw new UploadRequestError(
        getUploadTimeoutMessage('presign'),
        408,
        'presign'
      );
    }
    throw error;
  } finally {
    timeout.clear();
  }
}

async function uploadToPresignedUrl(
  file: File,
  presigned: PresignedUploadResult
): Promise<void> {
  const timeout = withTimeoutSignal(DIRECT_UPLOAD_TIMEOUT_MS);
  try {
    const headers: Record<string, string> = {
      ...(presigned.headers || {}),
    };
    if (!headers['Content-Type'] && file.type) {
      headers['Content-Type'] = file.type;
    }

    const response = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
      signal: timeout.signal,
    });

    if (!response.ok) {
      let message = 'Failed to upload file to storage';
      try {
        const text = await response.text();
        if (text.trim()) {
          message = text.slice(0, 500);
        }
      } catch {
        // ignore response parse errors
      }
      throw new UploadRequestError(message, response.status, 'direct-upload');
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw new UploadRequestError(
        getUploadTimeoutMessage('direct-upload'),
        408,
        'direct-upload'
      );
    }
    throw error;
  } finally {
    timeout.clear();
  }
}

function isMultipartUploadPlan(
  plan: PresignedUploadResult | BrowserMultipartUploadPlan
): plan is BrowserMultipartUploadPlan {
  return 'strategy' in plan && plan.strategy === 'multipart';
}

async function uploadMultipartPart(params: {
  file: File;
  plan: BrowserMultipartUploadPlan;
  partNumber: number;
  uploadUrl: string;
}) {
  const start = (params.partNumber - 1) * params.plan.partSize;
  const end = Math.min(start + params.plan.partSize, params.file.size);
  const body = params.file.slice(start, end);

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= MULTIPART_PART_MAX_ATTEMPTS; attempt += 1) {
    const timeout = withTimeoutSignal(MULTIPART_PART_TIMEOUT_MS);
    try {
      const response = await fetch(params.uploadUrl, {
        method: 'PUT',
        body,
        signal: timeout.signal,
      });
      if (response.ok) return;
      lastError = new UploadRequestError(
        `Multipart upload part failed (${response.status})`,
        response.status,
        'direct-upload'
      );
    } catch (error) {
      lastError = isAbortError(error)
        ? new UploadRequestError(
            getUploadTimeoutMessage('direct-upload'),
            408,
            'direct-upload'
          )
        : error;
    } finally {
      timeout.clear();
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new UploadRequestError(
        'Failed to upload multipart file',
        500,
        'direct-upload'
      );
}

async function postMultipartCommand<T = UploadFileResult>(
  url: string,
  body: Record<string, unknown>,
  allowEmptyResponse = false
): Promise<T | null> {
  const timeout = withTimeoutSignal(PRESIGN_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: timeout.signal,
    });
    if (!response.ok) {
      throw await parseApiError(
        response,
        'Failed to finalize multipart upload',
        'direct-upload'
      );
    }
    if (allowEmptyResponse) return null;
    return (await response.json()) as T;
  } catch (error) {
    if (isAbortError(error)) {
      throw new UploadRequestError(
        getUploadTimeoutMessage('direct-upload'),
        408,
        'direct-upload'
      );
    }
    throw error;
  } finally {
    timeout.clear();
  }
}

async function uploadMultipartParts(params: {
  file: File;
  plan: BrowserMultipartUploadPlan;
  parts: BrowserMultipartUploadPart[];
  onPartUploaded?: (partNumber: number) => void;
}) {
  let nextIndex = 0;
  const workerCount = Math.min(
    MULTIPART_UPLOAD_CONCURRENCY,
    params.parts.length
  );
  const results = await Promise.allSettled(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < params.parts.length) {
        const part = params.parts[nextIndex];
        nextIndex += 1;
        if (!part) return;
        await uploadMultipartPart({
          file: params.file,
          plan: params.plan,
          partNumber: part.partNumber,
          uploadUrl: part.uploadUrl,
        });
        params.onPartUploaded?.(part.partNumber);
      }
    })
  );
  const failed = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (failed) throw failed.reason;
}

async function abortMultipartUploadBestEffort(
  plan: BrowserMultipartUploadPlan
) {
  try {
    await postMultipartCommand(
      API_STORAGE_MULTIPART_ABORT,
      { key: plan.key, uploadId: plan.uploadId },
      true
    );
  } catch {
    // The original upload error is more useful to the caller.
  }
}

async function uploadMultipartFile(
  file: File,
  plan: BrowserMultipartUploadPlan,
  options?: BrowserUploadOptions
): Promise<UploadFileResult> {
  const partCount = plan.parts.length;
  const completedPartNumbers = new Set<number>();
  let pendingParts = plan.parts;
  let resumeAttempts = 0;
  const reportProgress = () =>
    emitUploadProgress(
      file,
      getCompletedMultipartBytes(file, plan, completedPartNumbers),
      options?.onProgress
    );
  reportProgress();

  try {
    for (;;) {
      try {
        await uploadMultipartParts({
          file,
          plan,
          parts: pendingParts,
          onPartUploaded: (partNumber) => {
            completedPartNumbers.add(partNumber);
            reportProgress();
          },
        });
        break;
      } catch (error) {
        if (resumeAttempts >= MULTIPART_SESSION_RESUME_MAX_ATTEMPTS) {
          throw error;
        }
        resumeAttempts += 1;
        const resumed = await postMultipartCommand<BrowserMultipartResumePlan>(
          API_STORAGE_MULTIPART_RESUME,
          {
            key: plan.key,
            uploadId: plan.uploadId,
            partCount,
          }
        );
        if (
          !resumed ||
          resumed.key !== plan.key ||
          resumed.uploadId !== plan.uploadId
        ) {
          throw new UploadRequestError(
            'Failed to resume multipart upload',
            500,
            'direct-upload'
          );
        }
        completedPartNumbers.clear();
        for (const partNumber of resumed.completedPartNumbers) {
          completedPartNumbers.add(partNumber);
        }
        reportProgress();
        pendingParts = resumed.parts;
        if (pendingParts.length === 0) break;
      }
    }

    const completed = await postMultipartCommand<UploadFileResult>(
      API_STORAGE_MULTIPART_COMPLETE,
      {
        key: plan.key,
        uploadId: plan.uploadId,
        partCount,
      }
    );
    if (!completed) {
      throw new UploadRequestError(
        'Failed to finalize multipart upload',
        500,
        'direct-upload'
      );
    }
    emitUploadProgress(file, file.size, options?.onProgress);
    return completed;
  } catch (error) {
    await abortMultipartUploadBestEffort(plan);
    throw error;
  }
}

/**
 * Uploads a file from the browser to the storage provider
 * This function is meant to be used in client components
 *
 * Flow: small files use one presigned PUT; large files use direct multipart PUTs.
 *
 * @param file - The file object from an input element
 * @param folder - Optional folder path to store the file in
 * @returns Promise with the URL of the uploaded file
 */
export const uploadFileFromBrowser = async (
  file: File,
  folder?: string,
  guestTask?: {
    pendingRunId: string;
    inputSlotId: string;
    paymentIntentId?: string;
    guestRecoveryToken?: string;
  },
  options?: BrowserUploadOptions
): Promise<UploadFileResult> => {
  try {
    const plan = await requestPresignedUpload(file, folder, guestTask);
    if (isMultipartUploadPlan(plan)) {
      return await uploadMultipartFile(file, plan, options);
    }
    emitUploadProgress(file, 0, options?.onProgress);
    await uploadToPresignedUrl(file, plan);
    emitUploadProgress(file, file.size, options?.onProgress);
    return {
      url: plan.publicUrl,
      key: plan.key,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred during file upload');
  }
};
